import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';
import { Messages } from '@dojo/framework/i18n/i18n';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import Button from '@dojo/widgets/button';

import { lccstore } from 'lcc-state';

import { QuizMeta, Question } from '../interfaces';

import css from '../css/pretest-review.m.css';

export interface PretestReviewProperties {
	quizstore: Store<QuizMeta>;
	isaudit: boolean;
	loaded?: boolean;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };

	getQuestions: () => Question[];
	onFinish: () => void;
	onReview: (flag: boolean) => void;
	filterOn: (name: 'marked' | 'complete' | 'incomplete' | 'all' | number) => void;
	checkFor: (name: 'marked' | 'complete' | 'incomplete' | 'all') => boolean;
}

@theme(css)
export class PretestReview extends I18nThemed<PretestReviewProperties> {
	private data: Question[] = [];
	private _inited: boolean = false;

	protected onAttach(): void {
		lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
	}

	@beforeProperties()
	ensure_nls(properties: PretestReviewProperties & WidgetProperties): PretestReviewProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		(() => {
			const { quizstore } = properties;
			const { get, path } = quizstore;
			properties.loaded = get(path('loaded'));
			if (!this._inited) {
				const invalidatePaths = [['loaded']].map((path: any) => quizstore.path(path.join('/')));
				const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
				this.own({
					destroy: () => {
						handle.remove();
					}
				});
				this._inited = true;
			}
		})();
		return properties;
	}

	protected render_heads(): DNode {
		const {
			localizedBundle: {
				messages: { question: questionText = '', incorrect: incorrectText = '', status: statusText = '' }
			}
		} = this.properties;

		return v(
			'div',
			{ key: 'head-block', role: 'rowgroup', classes: [this.theme(css.tableHead), this.theme(css.row)] },
			[
				v('div', { role: 'row' }, [
					v(
						'th',
						{
							key: 'head-question',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.question)]
						},
						[questionText]
					),
					v(
						'th',
						{
							key: 'head-marked',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.marked)]
						},
						[incorrectText]
					),
					v(
						'th',
						{
							key: 'head-status',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.status)]
						},
						[statusText]
					)
				])
			]
		);
	}

	protected render_row(question: Question): DNode {
		const {
			filterOn,
			onReview,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					status_complete = '[quiz] status_complete',
					status_incomplete = '[quiz] status_incomplete'
				}
			}
		} = this.properties;
		const { cursor, marked = false, flags } = question;
		const isOdd = !!(cursor % 2);
		const isComplete = flags[1];
		const isMarked = marked;
		return v('div', { role: 'row' }, [
			v(
				'a',
				{
					key: `row-${cursor}`,
					href: 'javascript:void;',
					classes: [this.theme(css.row), isOdd ? this.theme(css.oddRow) : null],
					onclick: (event: any) => {
						event && event.stopPropagation();
						filterOn(cursor);
						onReview(false);
					}
				},
				[
					v('div', { key: `q-${cursor}`, role: 'cell', classes: [this.theme(css.question)] }, [
						`${questionText} ${cursor + 1}`
					]),
					v('div', { key: `mrk-${cursor}`, role: 'cell', classes: [this.theme(css.marked)] }, [
						isMarked
							? v('i', {
									...formatAriaProperties({ label: incorrectText }),
									classes: ['fa', 'fa-check']
							  })
							: v('i', {}, [' '])
					]),
					v(
						'div',
						{
							role: 'cell button',
							key: `cmp-${cursor}`,
							classes: [
								this.theme(css.status),
								isComplete ? this.theme(css.complete) : this.theme(css.incomplete)
							]
						},
						[
							isComplete
								? v('i', {
										...formatAriaProperties({ label: status_complete }),
										classes: ['fa', 'fa-circle']
								  })
								: v('i', {
										...formatAriaProperties({ label: status_incomplete }),
										classes: ['fa', 'fa-exclamation-circle']
								  })
						]
					)
				]
			)
		]);
	}

	protected render_buttons(): DNode[] {
		const {
			onFinish,
			filterOn,
			checkFor,
			onReview,
			localizedBundle: {
				messages: {
					review_all: reviewAllText = '',
					review_incorrect: reviewIncorrectText = '',
					review_incomplete: reviewIncompleteText = '',
					end_review: finishText = ''
				}
			}
		} = this.properties;

		return [
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'all',
						onClick: () => {
							filterOn('all');
							onReview(false);
						}
					},
					[reviewAllText]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'marked',
						disabled: !checkFor('marked'),
						onClick: () => {
							filterOn('marked');
							onReview(false);
						}
					},
					[reviewIncorrectText]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'incomplete',
						disabled: !checkFor('incomplete'),
						onClick: () => {
							filterOn('incomplete');
							onReview(false);
						}
					},
					[reviewIncompleteText]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'finish',
						onClick: () => {
							onFinish();
						}
					},
					[finishText]
				)
			]),
			v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [])
		];
	}

	protected render_review(): DNode[] {
		const {
			getQuestions,
			loaded = false,
			localizedBundle: {
				messages: { review_instructions = '' }
			}
		} = this.properties;

		if (!loaded) {
			v('div', { key: 'loading', classes: [this.theme(css.loading)] }, [
				v('i', { classes: ['fas', 'fa-spinner', 'fa-spin'] })
			]);
		}

		if (this.data.length === 0) {
			this.data = getQuestions();
		}
		const { data } = this;
		const kids = data.map<DNode>((q) => this.render_row(q));

		return [
			v('div', { key: 'buttons', classes: [this.theme(css.buttons)] }, this.render_buttons()),
			v('div', { key: 'instruct', classes: [this.theme(css.instruct)] }, [review_instructions]),
			v('div', { key: 'review-table', role: 'table', classes: [this.theme(css.table)], tabindex: '0' }, [
				this.render_heads(),
				v('div', { role: 'rowgroup' }, [...kids])
			])
		];
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const xqa = `review pretest-audit`;

		return v('div', { key: 'review', xqa, classes: [this.theme(css.root)] }, this.render_review());
	}
}

export default PretestReview;
